import { create } from 'zustand';

export const useQuoteStore = create((set, get) => ({
  currentStep: 1,
  measurementType: '',
  blendAreas: [{
    surface_blend: '',
    quantity: '',
    specification: '',
    base_mixes: ''
  }],
  border: null,
  wastage: '',
  requiresMesh: null,
  meshRolls: null,
  ancillaries: {
    trowel_lubricant: 0,
    primer: 0,
    crushed_glass: 0,
    isopropanol: 0,
    quattromix: 0,
    rigid_trim: 0,
    formable_trim: 0
  },
  customerDetails: {
    name: '',
    email: '',
    phone: '',
    customer_name: '',
    address: '',
    start_date: ''
  },

  setCurrentStep: (step) => set({ currentStep: step }),
  
  setMeasurementType: (type) => set({ measurementType: type }),
  
  setBlendAreas: (areas) => set({ blendAreas: areas }),
  
  setBorder: (border) => set({ border }),
  
  addBlendArea: () => set((state) => ({
    blendAreas: [
      ...state.blendAreas,
      { surface_blend: '', quantity: '', specification: '', base_mixes: '' }
    ]
  })),
  
  removeBlendArea: (index) => set((state) => ({
    blendAreas: state.blendAreas.filter((_, i) => i !== index)
  })),
  
  updateBlendArea: (index, field, value) => set((state) => ({
    blendAreas: state.blendAreas.map((area, i) => 
      i === index ? { ...area, [field]: value } : area
    )
  })),
  
  setWastage: (wastage) => set({ wastage }),
  
  setRequiresMesh: (requires) => set({ requiresMesh: requires }),
  
  setMeshRolls: (rolls) => set({ meshRolls: rolls }),
  
  updateAncillary: (id, quantity) => set((state) => ({
    ancillaries: { ...state.ancillaries, [id]: quantity }
  })),
  
  setCustomerDetails: (details) => set({ customerDetails: details }),
  
  updateCustomerDetail: (field, value) => set((state) => ({
    customerDetails: { ...state.customerDetails, [field]: value }
  })),

  // Calculate total quantity in original units
  getTotalQuantity: () => {
    const { blendAreas } = get();
    return blendAreas.reduce((sum, area) => {
      const quantity = parseFloat(area.quantity) || 0;
      return sum + quantity;
    }, 0);
  },

  // Convert mixes to m² based on specification
  convertToM2: (quantity, specification) => {
    const { measurementType } = get();
    
    if (measurementType === 'Square metres (m²)') {
      return quantity;
    }
    
    // Conversion rates for mixes
    const conversionRates = {
      'Vehicle traffic - surface only': 3.5,
      'Vehicle traffic - surface and base': 3.5 + 1.75, // surface + base
      'Foot traffic - surface only': 4.0,
      'Foot traffic - surface and base': 4.0 + 3.5 // surface + base
    };
    
    return quantity * (conversionRates[specification] || 1);
  },

  // Calculate total m² (accounting for specification)
  getTotalM2: () => {
    const { blendAreas } = get();
    const convertToM2 = get().convertToM2;
    
    return blendAreas.reduce((sum, area) => {
      const quantity = parseFloat(area.quantity) || 0;
      const m2 = convertToM2(quantity, area.specification);
      return sum + m2;
    }, 0);
  },

  // Get wastage percentage as decimal
  getWastagePercentage: () => {
    const { wastage } = get();
    if (wastage === '10%') return 0.1;
    if (wastage === '20%') return 0.2;
    return 0;
  },

  // Calculate total with wastage
  getTotalWithWastage: () => {
    const totalQuantity = get().getTotalQuantity();
    const wastagePercentage = get().getWastagePercentage();
    return totalQuantity * (1 + wastagePercentage);
  },

  // Calculate mesh rolls needed
  getMeshRolls: () => {
    const totalM2 = get().getTotalM2();
    const wastagePercentage = get().getWastagePercentage();
    const totalM2WithWastage = totalM2 * (1 + wastagePercentage);
    return Math.ceil(totalM2WithWastage / 100);
  },

  // Reset store
  reset: () => set({
    currentStep: 1,
    measurementType: '',
    blendAreas: [{ surface_blend: '', quantity: '', specification: '', base_mixes: '' }],
    border: null,
    wastage: '',
    requiresMesh: null,
    meshRolls: null,
    ancillaries: {
      trowel_lubricant: 0,
      primer: 0,
      crushed_glass: 0,
      isopropanol: 0,
      quattromix: 0,
      rigid_trim: 0,
      formable_trim: 0
    },
    customerDetails: {
      name: '',
      email: '',
      phone: '',
      customer_name: '',
      address: '',
      start_date: ''
    }
  })
}));